/**
 * Buscar y ordenar listas del panel.
 *
 * Existe para las listas de personal y de recursos, que crecen hasta donde
 * crece el negocio: con veinte personas cargadas, encontrar una es scrollear
 * hasta verla, y el orden en que aparecen es el orden en que se fueron
 * agregando —o sea ninguno—.
 *
 * La lógica vive acá y no adentro de Dashboard.jsx por dos razones: son las
 * mismas dos operaciones para las dos listas, y son las únicas dos piezas de
 * esa pantalla que se pueden razonar solas, sin estado ni React.
 *
 * Sobre normalizar el texto: acá hay una tercera versión de "sacar tildes y
 * mayúsculas", después de `normalizarRol` (lib/roles.js) y la de
 * lib/servicios.js. No es un descuido. Esas dos deciden IDENTIDAD —si dos
 * nombres de servicio chocan, si dos roles son el mismo puesto— y cambiar su
 * regla cambia qué cosas se consideran repetidas, con consecuencias en los
 * datos. Esta decide si algo aparece mientras se escribe en un buscador, donde
 * ser más laxo no rompe nada. Unificarlas ataría una cosa a la otra: aflojar la
 * búsqueda pasaría a permitir servicios duplicados.
 */

/** Minúsculas, sin tildes y sin espacios de más. Para comparar al buscar. */
export const normalizarTexto = (texto) =>
  String(texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const ORDEN_ALFABETICO = 'alfabetico';
export const ORDEN_POR_ETIQUETA = 'etiqueta';

/**
 * Los que coinciden con lo escrito.
 *
 * `camposDe` devuelve todo el texto por el que una fila se puede encontrar: el
 * nombre, pero también el mail, el teléfono o el rol. Se busca por partes y no
 * por "empieza con", porque quien escribe "gonz" está buscando a González y
 * quien escribe "1123" está buscando un teléfono por el medio.
 *
 * Con el buscador vacío devuelve la lista igual, sin copiarla.
 */
export const buscarEnLista = (items = [], texto = '', camposDe = (i) => [i?.name]) => {
  const aguja = normalizarTexto(texto);
  if (!aguja) return items;

  // Cada palabra por separado: "juan perez" encuentra a "Pérez, Juan Manuel",
  // que con la frase entera como una sola aguja no aparecería.
  const palabras = aguja.split(' ');

  return items.filter((item) => {
    const pajar = normalizarTexto((camposDe(item) || []).filter(Boolean).join(' '));
    return palabras.every((palabra) => pajar.includes(palabra));
  });
};

/**
 * Ordenados alfabéticamente, o agrupados por su etiqueta.
 *
 * Por etiqueta significa: primero todas las de una etiqueta, después las de la
 * siguiente, y adentro de cada grupo por nombre. Quien tiene varias entra por
 * la primera, que es la que la tarjeta muestra adelante.
 *
 * Los que no tienen ninguna van al final y no al principio: son la excepción, y
 * arriba de todo desplazarían a los que sí están clasificados. `localeCompare`
 * con 'es' para que la Ñ caiga entre la N y la O, y no después de la Z.
 *
 * Devuelve una copia: `sort` ordena en el lugar, y sobre el arreglo del estado
 * de React sería mutarlo.
 */
export const ordenarLista = (items = [], modo = ORDEN_ALFABETICO, etiquetasDe = () => []) => {
  const porNombre = (a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'es');

  if (modo !== ORDEN_POR_ETIQUETA) return [...items].sort(porNombre);

  const primeraEtiqueta = (item) => (etiquetasDe(item) || []).filter(Boolean)[0] || '';

  return [...items].sort((a, b) => {
    const ea = primeraEtiqueta(a);
    const eb = primeraEtiqueta(b);
    if (!ea && !eb) return porNombre(a, b);
    if (!ea) return 1; // sin etiqueta, al final
    if (!eb) return -1;
    const comparadas = ea.localeCompare(eb, 'es');
    return comparadas !== 0 ? comparadas : porNombre(a, b);
  });
};

/** Buscar y ordenar de una, que es como se usan siempre. */
export const filtrarYOrdenar = (items, { texto, orden, camposDe, etiquetasDe } = {}) =>
  ordenarLista(buscarEnLista(items, texto, camposDe), orden, etiquetasDe);
